import { useState } from 'react';
import { useGlobal } from '@/context/GlobalContext';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Textarea } from '@/components/ui/textarea';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Label } from '@/components/ui/label';
import { toast } from 'sonner';
import { Send, CheckCircle, Mail, Phone, MapPin, Calendar } from 'lucide-react';
import axios from 'axios';
import SEO from '@/components/SEO';

const ContactPage = () => {
  const { settings, API } = useGlobal();
  const [loading, setLoading] = useState(false);
  const [submitted, setSubmitted] = useState(false);
  const [formData, setFormData] = useState({
    name: '',
    company: '',
    email: '',
    project_type: '',
    message: ''
  });

  const projectTypes = [
    'Mobility Imaging',
    'Ultrasonic Animal Deterrence',
    'Surveillance',
    'Other'
  ];

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);

    try {
      await axios.post(`${API}/contact`, formData);
      setSubmitted(true);
      toast.success('Message sent successfully!');
    } catch (error) {
      console.error('Error submitting form:', error);
      toast.error('Failed to send message. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  if (submitted) {
    return (
      <div className="min-h-screen bg-[#1a1f2e]" data-testid="contact-page">
        <section className="page-hero pt-32">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            <h1 className="text-4xl sm:text-5xl font-bold text-white mb-6 font-['Montserrat']">
              Thank You
            </h1>
          </div>
        </section>
        <section className="py-24">
          <div className="max-w-xl mx-auto px-4 text-center">
            <div className="w-20 h-20 bg-green-900/30 border border-green-500/30 rounded-full flex items-center justify-center mx-auto mb-6">
              <CheckCircle className="w-10 h-10 text-green-400" />
            </div>
            <h2 className="text-2xl font-bold text-white mb-4 font-['Montserrat']">
              Message Received
            </h2>
            <p className="text-slate-400 mb-8">
              Thank you for contacting us. Our team will review your inquiry 
              and get back to you within 1-2 business days.
            </p>
            <a href={settings.teams_scheduling_url} target="_blank" rel="noopener noreferrer">
              <Button className="bg-[#5b3589] hover:bg-[#6d3d9e] text-white rounded-full">
                <Calendar className="w-4 h-4 mr-2" />
                Or Schedule a Call Now
              </Button>
            </a>
          </div>
        </section>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-[#1a1f2e]" data-testid="contact-page">
      <SEO 
        title="Contact Us - Start Your Project"
        description="Contact Trinity Electronics for OEM/ODM imaging solutions, surveillance systems, or ultrasonic deterrence. Located in Taiwan, serving worldwide. Schedule a call or send an inquiry."
        keywords="contact Trinity Electronics, OEM ODM inquiry, camera manufacturer contact, Taiwan imaging company, schedule meeting, product inquiry"
        canonicalUrl="https://www.trinity-tw.com/contact"
      />
      
      {/* Hero Section */}
      <section className="page-hero pt-32 pb-16">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <h1 className="text-4xl sm:text-5xl font-bold text-white mb-6 font-['Montserrat']">
            Let's Build Together
          </h1>
          <p className="text-lg text-slate-400 max-w-2xl mx-auto">
            Share your requirements for OEM/ODM or request product details — our team will respond with the best next steps.
          </p>
        </div>
      </section>

      {/* Contact Form Section */}
      <section className="py-16 bg-[#141824]">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="feature-card p-8">
            <form onSubmit={handleSubmit} className="contact-form-dark space-y-6" data-testid="contact-form">
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-6">
                <div className="space-y-2">
                  <Label htmlFor="name" className="text-slate-300">Your Name (required)</Label>
                  <Input
                    id="name"
                    name="name"
                    value={formData.name}
                    onChange={handleChange}
                    required
                    placeholder="Your name"
                    className="bg-[#1a1f2e] border-slate-700 text-white placeholder-slate-500 focus:border-[#5b3589]"
                    data-testid="contact-name"
                  />
                </div>
                <div className="space-y-2">
                  <Label htmlFor="company" className="text-slate-300">Company Name</Label>
                  <Input
                    id="company"
                    name="company"
                    value={formData.company}
                    onChange={handleChange}
                    placeholder="Company name"
                    className="bg-[#1a1f2e] border-slate-700 text-white placeholder-slate-500 focus:border-[#5b3589]"
                    data-testid="contact-company"
                  />
                </div>
              </div>

              <div className="grid grid-cols-1 sm:grid-cols-2 gap-6">
                <div className="space-y-2">
                  <Label htmlFor="email" className="text-slate-300">Email Address (required)</Label>
                  <Input
                    id="email"
                    name="email"
                    type="email"
                    value={formData.email}
                    onChange={handleChange}
                    required
                    placeholder="your@email.com"
                    className="bg-[#1a1f2e] border-slate-700 text-white placeholder-slate-500 focus:border-[#5b3589]"
                    data-testid="contact-email"
                  />
                </div>
                <div className="space-y-2">
                  <Label htmlFor="project_type" className="text-slate-300">Project Type (required)</Label>
                  <Select 
                    value={formData.project_type} 
                    onValueChange={(value) => setFormData(prev => ({ ...prev, project_type: value }))}
                  >
                    <SelectTrigger className="bg-[#1a1f2e] border-slate-700 text-white" data-testid="contact-project-type">
                      <SelectValue placeholder="Select project type" />
                    </SelectTrigger>
                    <SelectContent className="bg-[#151b23] border-slate-700">
                      {projectTypes.map(type => (
                        <SelectItem key={type} value={type} className="text-slate-300 hover:bg-slate-700">{type}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
              </div>

              <div className="space-y-2">
                <Label htmlFor="message" className="text-slate-300">Message (required)</Label>
                <Textarea
                  id="message"
                  name="message"
                  value={formData.message}
                  onChange={handleChange}
                  required
                  rows={6}
                  placeholder="Tell us about your project requirements..."
                  className="bg-[#1a1f2e] border-slate-700 text-white placeholder-slate-500 focus:border-[#5b3589] resize-none"
                  data-testid="contact-message"
                />
              </div>

              <p className="text-slate-500 text-sm">
                We can respond in English, Mandarin, German, French, Russian, Swedish, or Japanese.
              </p>

              <Button 
                type="submit" 
                disabled={loading || !formData.project_type}
                className="w-full bg-[#5b3589] hover:bg-[#6d3d9e] text-white rounded-full py-6"
                data-testid="contact-submit"
              >
                {loading ? (
                  'Sending...'
                ) : (
                  <>
                    <Send className="w-4 h-4 mr-2" />
                    Send Message
                  </>
                )}
              </Button>
            </form>
          </div>

          {/* Contact Info */}
          <div className="mt-12 grid grid-cols-1 md:grid-cols-3 gap-6">
            <a 
              href={`mailto:${settings.company_email}`}
              className="feature-card flex items-center gap-4 group"
            >
              <div className="w-12 h-12 bg-[#5b3589]/20 rounded-lg flex items-center justify-center group-hover:bg-[#5b3589]/30 transition-colors">
                <Mail className="w-5 h-5 text-[#a78bfa]" />
              </div>
              <div>
                <div className="text-white font-medium">Email</div>
                <div className="text-slate-400 text-sm">{settings.company_email}</div>
              </div>
            </a>

            <div className="feature-card flex items-center gap-4">
              <div className="w-12 h-12 bg-[#5b3589]/20 rounded-lg flex items-center justify-center">
                <MapPin className="w-5 h-5 text-[#a78bfa]" />
              </div>
              <div>
                <div className="text-white font-medium">Office</div>
                <div className="text-slate-400 text-sm">{settings.company_address}</div>
              </div>
            </div>

            <a 
              href={settings.teams_scheduling_url}
              target="_blank"
              rel="noopener noreferrer"
              className="feature-card flex items-center gap-4 group"
            >
              <div className="w-12 h-12 bg-[#5b3589]/20 rounded-lg flex items-center justify-center group-hover:bg-[#5b3589]/30 transition-colors">
                <Calendar className="w-5 h-5 text-[#a78bfa]" />
              </div>
              <div>
                <div className="text-white font-medium">Schedule Call</div>
                <div className="text-slate-400 text-sm">Book a Teams meeting</div>
              </div>
            </a>
          </div>
        </div>
      </section>
    </div>
  );
};

export default ContactPage;
